import React from "react";
import { toast } from "sonner";
import { Loader2, Package, RefreshCw, Store } from "lucide-react";
import { adminApi } from "../../lib/api";

export default function DistributionPakkeshopPanel() {
  const [partners, setPartners] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [collectLabel, setCollectLabel] = React.useState("");
  const [collectHub, setCollectHub] = React.useState("roskilde");
  const [collectBusy, setCollectBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/distribution/pakkeshop/overview");
      setPartners(data.partners || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente pakkeshop-overblik");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const collect = async (e) => {
    e.preventDefault();
    if (!collectLabel.trim()) return;
    setCollectBusy(true);
    try {
      const { data } = await adminApi().post("/admin/distribution/pakkeshop/collect", {
        label_number: collectLabel.trim().toUpperCase(),
        hub_id: collectHub,
      });
      toast.success(data.message || "Hentet fra shop");
      setCollectLabel("");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Kunne ikke registrere afhentning");
    } finally {
      setCollectBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-brand-text2 py-8" data-testid="dist-ps-loading">
        <Loader2 className="animate-spin" size={18} /> Indlæser pakkeshops...
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="distribution-pakkeshop-panel">
      <div className="bg-white border border-brand p-5">
        <div className="flex items-center gap-3">
          <Store size={20} className="text-brand-red" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">Distribution · Pakkeshop</div>
            <div className="font-display text-lg text-brand-dark">Kapacitet & afhentning til HUB</div>
          </div>
          <button type="button" onClick={load} className="px-3 py-1.5 text-xs border border-brand flex items-center gap-1">
            <RefreshCw size={12} /> Opdater
          </button>
        </div>
      </div>

      <form onSubmit={collect} className="bg-white border border-brand p-5 space-y-3">
        <div className="text-sm font-semibold text-brand-dark">EMA afhenter drop-off fra shop</div>
        <p className="text-xs text-brand-text2">
          Scan label efter fysisk afhentning i butikken. Pakken er klar til inbound-scan på valgt HUB.
        </p>
        <div className="flex flex-wrap gap-3">
          <input
            className="input-flat flex-1 min-w-[200px] font-mono"
            placeholder="Label / EMA-P…"
            value={collectLabel}
            onChange={(e) => setCollectLabel(e.target.value)}
            data-testid="dist-ps-collect-label"
          />
          <select className="input-flat w-40" value={collectHub} onChange={(e) => setCollectHub(e.target.value)}>
            <option value="roskilde">Roskilde</option>
            <option value="koege">Køge</option>
            <option value="nykoebing_f">Nykøbing F</option>
            <option value="bornholm_ronne">Bornholm</option>
          </select>
          <button type="submit" disabled={collectBusy} className="btn-primary px-4 py-2 text-sm">
            {collectBusy ? <Loader2 className="animate-spin" size={14} /> : "Registrér afhentning"}
          </button>
        </div>
      </form>

      <div className="bg-white border border-brand overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg text-xs uppercase tracking-wider text-brand-text2">
            <tr>
              <th className="text-left p-3">Shop</th>
              <th className="text-left p-3">Kapacitet</th>
              <th className="text-left p-3">Drop-offs venter</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {partners.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-brand-text2">Ingen partnere</td>
              </tr>
            )}
            {partners.map((p) => (
              <tr key={p.id} className="border-t border-brand">
                <td className="p-3">
                  <div className="font-semibold text-brand-dark">{p.shop_name}</div>
                  <div className="text-xs text-brand-text2">{p.shop_zip} {p.shop_city}</div>
                </td>
                <td className="p-3">
                  <span className={p.is_full ? "text-red-600 font-semibold" : ""}>
                    {p.current_parcels} / {p.max_parcels}
                  </span>
                  <div className="text-xs text-brand-text2">{p.available_slots} ledige</div>
                </td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-1">
                    <Package size={12} /> {p.dropoffs_waiting || 0}
                  </span>
                </td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100"}`}>
                    {p.active ? "Aktiv" : "Inaktiv"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
